import placeIcons, {defaultPlaceIcon} from '@gaia/placeIcons';
import Autosuggest, {AutosuggestPropsSingleSection as AutosuggestProps} from 'react-autosuggest';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import {useState, useCallback, useRef} from 'react';
import theme from '@gaia/theme';
import memoize from 'lodash/memoize';
import trackAnalytics from '@gaia/utils/gaia/trackAnalytics';
import fetchJSON from '@gaia/utils/fetchJSON';
import qs from 'qs';
import useGetSyncPromise from '@gaia/utils/react/useGetSyncPromise';
import parseCoordinates from '@gaia/utils/parseCoordinates';
import {searchZIndex, searchSuggestionsZIndex} from '@gaia/utils/gaia/zIndices';
import LinearProgress from '@mui/material/LinearProgress';

export interface SearchBoxProps
    extends Omit<Partial<AutosuggestProps<SearchResult>>, 'inputProps'> {
    onSearchResultSelected?: (
        event: React.FormEvent<any>,
        data: Autosuggest.SuggestionSelectedEventData<SearchResult>,
        url: string
    ) => void;
    center?: mapboxgl.LngLat | undefined;
    style?: React.CSSProperties;
    inputProps?: Partial<AutosuggestProps<SearchResult>['inputProps']>;
    resultsContainerStyle?: React.CSSProperties;
}

const fetchSuggestions = memoize(
    async (
        query: string,
        center: mapboxgl.LngLat | undefined,
        pressed_enter: boolean
    ): Promise<SearchResult[]> => {
        if (query.length < 3) return [];

        const url = `${DEV_PROXY}/api/trip/autocomplete?${qs.stringify({
            query,
            pressed_enter,
            ...(center && {
                lat: center.lat.toFixed(3),
                lon: center.lng.toFixed(3),
            }),
        })}`;

        const {results} = await fetchJSON(url);

        trackAnalytics('Fetched Suggestions', {
            category: 'Hike',
            resultCount: results.length,
            query,
        });

        return results;
    },
    (...args) => JSON.stringify(args)
);

function getSearchResultUrl(searchResult: SearchResult) {
    if (searchResult.permalink) {
        return searchResult.permalink;
    } else if (searchResult.type === 'known_route') {
        return `/hike/${searchResult.known_route_id}`;
    } else {
        return (
            '/hike/nearby/' +
            `?lat=${searchResult.location[1]}` +
            `&lon=${searchResult.location[0]}` +
            `&name=${searchResult.title}`
        );
    }
}

export default function SearchBox({onSearchResultSelected, center, ...props}: SearchBoxProps) {
    const getSyncPromise = useGetSyncPromise();
    const [value, setValue] = useState('');
    const [coordinateSuggestions, setCoordinateSuggestions] = useState<SearchResult[]>([]);
    const [suggestionsPromise, setSuggestionsPromise] = useState<Promise<SearchResult[]>>();

    const clearSuggestions = useCallback(() => {
        setValue('');
        setSuggestionsPromise(undefined);
        setCoordinateSuggestions([]);
    }, []);

    const [suggestions, , isSuggestionsLoaded] = suggestionsPromise
        ? getSyncPromise(suggestionsPromise)
        : [undefined, undefined, true];

    const ref = useRef<any>();
    const inputWidth = props.inputProps?.style?.width || '100%';

    const renderSuggestionsContainer = useCallback(
        (options: Autosuggest.RenderSuggestionsContainerParams) => {
            return (
                <Paper
                    {...options.containerProps}
                    aria-label='Search results'
                    className={options.containerProps.className}
                    style={{
                        position: 'absolute',
                        zIndex: searchSuggestionsZIndex,
                        // 5 list items, approximately
                        maxHeight: 330,
                        minWidth: '100%',
                        maxWidth: '125%',
                        overflow: 'auto',
                        ...props.resultsContainerStyle,
                    }}
                >
                    {options.children}
                </Paper>
            );
        },
        [props.resultsContainerStyle]
    );

    return (
        <div
            style={{
                ...props.style,
                position: 'relative',
                // Make room for progress bar to display beneath input without reflowing the DOM
                paddingBottom: 4,
            }}
            className='mapboxgl-ctrl'
        >
            <Autosuggest
                suggestions={[...coordinateSuggestions, ...(suggestions || [])]}
                onSuggestionsFetchRequested={(
                    params: Autosuggest.SuggestionsFetchRequestedParams
                ) => {
                    if (props.onSuggestionsFetchRequested)
                        props.onSuggestionsFetchRequested(params);
                    setCoordinateSuggestions(getCoordinateSuggestions(params.value));
                    setSuggestionsPromise(fetchSuggestions(params.value, center, false));
                }}
                getSuggestionValue={(suggestion: SearchResult) => suggestion.title}
                renderSuggestion={renderSuggestion}
                renderSuggestionsContainer={renderSuggestionsContainer}
                onSuggestionSelected={(
                    event: React.FormEvent<any>,
                    data: Autosuggest.SuggestionSelectedEventData<SearchResult>
                ) => {
                    const url = getSearchResultUrl(data.suggestion);

                    trackAnalytics('Chose Suggestion', {
                        category: 'Hike',
                        url,
                        query: value,
                        name: data.suggestion.title,
                    });

                    if (onSearchResultSelected) {
                        onSearchResultSelected(event, data, url);
                    } else {
                        window.location.href = url;
                    }

                    clearSuggestions();
                }}
                onSuggestionsClearRequested={clearSuggestions}
                {...props}
                ref={ref}
                inputProps={{
                    'aria-label': 'Search',
                    onChange: (event, {newValue}) => setValue(newValue),
                    onKeyDown: (event) => {
                        if (event.key === 'Enter') {
                            if (ref.current && !ref.current.getHighlightedSuggestion()) {
                                setSuggestionsPromise(fetchSuggestions(value, center, true));
                                ref.current.setState({isCollapsed: false});
                            }
                        }
                    },
                    onFocus: () => {
                        trackAnalytics('Selected Search Box', {
                            category: 'Hike',
                        });
                    },
                    value: value,
                    placeholder: 'Search',
                    autoComplete: 'off',
                    spellCheck: false,
                    autoCorrect: 'off',
                    autoCapitalize: 'off',
                    ...props.inputProps,
                    style: {
                        borderWidth: 2,
                        borderColor: 'rgba(0, 0, 0, 0.1)',
                        borderRadius: theme.shape.borderRadius,
                        boxSizing: 'border-box',
                        fontFamily: theme.typography.fontFamily,
                        fontSize: 14,
                        height: 40,
                        minWidth: 260,
                        paddingBottom: theme.spacing(1),
                        paddingLeft: theme.spacing(1.5),
                        paddingRight: theme.spacing(1.5),
                        paddingTop: theme.spacing(1),
                        position: 'relative',
                        width: inputWidth,
                        zIndex: searchZIndex,
                        ...props.inputProps?.style,
                    },
                }}
                theme={{
                    suggestionsList: {
                        listStyleType: 'none',
                        padding: 0,
                        margin: 0,
                    },
                    ...props.theme,
                }}
            />
            {!isSuggestionsLoaded && (
                <div
                    style={{
                        width: inputWidth,
                        boxSizing: 'border-box',
                        paddingLeft: theme.spacing(0.5),
                        paddingRight: theme.spacing(0.5),
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                    }}
                >
                    <LinearProgress />
                </div>
            )}
        </div>
    );
}

function getCoordinateSuggestions(query: string): SearchResult[] {
    const coordinates = parseCoordinates(query);
    if (!coordinates) return [];

    const xDirection = coordinates[1] > 0 ? 'E' : 'W';
    const yDirection = coordinates[0] > 0 ? 'N' : 'S';

    const x = `${1 * (Math.abs(coordinates[1]).toFixed(6) as any)}°${xDirection}`;
    const y = `${1 * (Math.abs(coordinates[0]).toFixed(6) as any)}°${yDirection}`;

    return [
        {
            location: [coordinates[1], coordinates[0]],
            bounds: [coordinates[1], coordinates[0], coordinates[1], coordinates[0]],
            id: null,
            known_route_id: null,
            permalink_api: null,
            permalink: `/hike/nearby/?lat=${coordinates[0]}&lon=${coordinates[1]}&name=${query}`,
            subtitle: 'Coordinate',
            subtype: 'coordinate',
            title: `${y} ${x}`,
            type: 'coordinate',
            osm_id: null,
        },
    ];
}

function renderSuggestion(
    searchResult: SearchResult,
    {isHighlighted}: Autosuggest.RenderSuggestionParams
) {
    const {title, subtitle, subtype} = searchResult;
    return (
        <MenuItem selected={isHighlighted} component='div'>
            <ListItemIcon>
                {placeIcons[subtype as keyof typeof placeIcons] || defaultPlaceIcon}
            </ListItemIcon>
            <ListItemText
                primary={title}
                secondary={subtitle}
                primaryTypographyProps={{
                    style: {
                        fontSize: 14,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                    title,
                }}
                secondaryTypographyProps={{
                    style: {
                        fontSize: 12,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                }}
            />
        </MenuItem>
    );
}
